import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { GraphQLClient, GraphQLClientError } from '@opendoor/auth-fe/context';
import type { GraphQLError } from 'graphql';
import { ClientError } from 'graphql-request';
import type { GraphQLClient as GraphQLRequestClient } from 'graphql-request';

/**
 * graphql-request's `ClientError.message` is `${msg}: ${JSON.stringify({ request, response })}` —
 * it embeds request variables (e.g. the plaintext `newPassword` on the reset-password mutation).
 * This error carries a stable message and only the server-provided GraphQL errors + network status,
 * so nothing the adapter returns can leak request payload into telemetry or the auth state.
 */
class FetchGqlError extends Error implements GraphQLClientError {
  graphQLErrors: readonly GraphQLError[];
  networkError: { status?: number; statusText?: string };
  constructor(
    message: string,
    errors: ReadonlyArray<GraphQLError>,
    networkError: { status?: number; statusText?: string },
  ) {
    super(message);
    this.graphQLErrors = errors;
    this.networkError = networkError;
  }
}

/**
 * Adapts cosmos's configured graphql-request athena client to auth-fe's injectable `GraphQLClient`
 * seam. `query` never rejects — transport and GraphQL failures resolve into the `{ data, error }`
 * envelope with a stable, leak-safe error.
 */
export const fromGraphqlRequestClient = (
  client: Pick<GraphQLRequestClient, 'request'>,
): GraphQLClient => ({
  async query<TData, TVars extends Record<string, unknown>>(
    document: TypedDocumentNode<TData, TVars>,
    vars: TVars,
  ) {
    try {
      const data = await client.request<TData, Record<string, unknown>>(document, vars);
      if (data == null) {
        return {
          data: undefined,
          error: new FetchGqlError('Graphql response had no data', [], {}),
        };
      }
      return { data, error: undefined };
    } catch (error) {
      if (error instanceof ClientError) {
        const graphQLErrors = error.response.errors ?? [];
        return {
          data: undefined,
          error: new FetchGqlError(
            graphQLErrors.length > 0 ? 'Graphql request had errors' : 'Failed to fetch',
            graphQLErrors,
            { status: error.response.status },
          ),
        };
      }
      return { data: undefined, error: new FetchGqlError('Failed to fetch', [], {}) };
    }
  },
});
